import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { Tabs, TabsContent } from '../../../components/ui/tabs';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { RefreshCw, AlertCircle } from 'lucide-react';

import { YoolPageScaffold, YoolPageHeader, YoolSection } from '../../../components/YoolPageScaffold';

import CompanyDashboardCharts from './charts/CompanyDashboardCharts';
import CompanyDashboardNavigation from './navigation/CompanyDashboardNavigation';
import CompanyMetricsGrid from './metrics/CompanyMetricsGrid';

import ConversionFunnelChart from '../../../components/dashboard/charts/ConversionFunnelChart';
import MetricsTrendChart from '../../../components/dashboard/charts/MetricsTrendChart';
import ProductPerformanceChart from '../../../components/dashboard/charts/ProductPerformanceChart';
import DetailedMetricsTable from '../../../components/dashboard/tables/DetailedMetricsTable';

import SessionsAnalysis from '../../../components/dashboard/detailed/SessionsAnalysis';
import QuotationsAnalysis from '../../../components/dashboard/detailed/QuotationsAnalysis';
import ContractsAnalysis from '../../../components/dashboard/detailed/ContractsAnalysis';
import CertificatesAnalysis from '../../../components/dashboard/detailed/CertificatesAnalysis';
import SubscriptionsAnalysis from '../../../components/dashboard/detailed/SubscriptionsAnalysis';

import EnhancedDashboardService from '../../../services/dashboard/enhancedDashboardService';

const CompanyDashboard = () => {
  const { id: companyId } = useParams();
  const navigate = useNavigate();
  const { session } = useSession();

  const [company, setCompany] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [productPerformance, setProductPerformance] = useState([]);
  const [planPerformance, setPlanPerformance] = useState([]);
  const [conversionFunnel, setConversionFunnel] = useState(null);
  const [funnelSource, setFunnelSource] = useState(null);
  const [trendsData, setTrendsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('overview');
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [currentLevel, setCurrentLevel] = useState('Company Overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');
  const [timeframe, setTimeframe] = useState('30d');

  const [dashboardService, setDashboardService] = useState(null);

  useEffect(() => {
    if (session) {
      setDashboardService(new EnhancedDashboardService(session));
    }
  }, [session]);

  useEffect(() => {
    if (dashboardService && companyId) {
      loadDashboardData();
    }
  }, [dashboardService, companyId, timeframe]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const metricsData = await dashboardService.getCompanyMetrics(companyId, timeframe);
      const productData = await dashboardService.getProductPerformance(companyId);
      const planData = await dashboardService.getPlanPerformance(companyId);
      const funnelData = await dashboardService.getConversionFunnel(companyId, timeframe);
      const sourceData = await dashboardService.getCompanyViewsSource(companyId, timeframe);

      setMetrics(metricsData);
      setProductPerformance(productData);
      setPlanPerformance(planData);
      setConversionFunnel(funnelData);
      setFunnelSource(sourceData);

      const trends = await generateTrendData(metricsData);
      setTrendsData(trends);

      setBreadcrumb([
        { name: 'Dashboard', label: 'Dashboard', level: 'root' },
        { name: 'Company', label: `Compañía ${companyId}`, level: 'company' },
      ]);
    } catch (err) {
      setError(`Error al cargar los datos del dashboard: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const generateTrendData = async (metricsData) => {
    if (!metricsData || !dashboardService) return [];
    try {
      const data = await dashboardService.getMetricsTrends(companyId, timeframe);
      if (data && data.length > 0) return data;

      const days = parseInt(timeframe.replace('d', ''));
      const fallback = [];
      const baseDate = new Date();
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(baseDate);
        date.setDate(date.getDate() - i);
        fallback.push({
          date: date.toISOString(),
          sessions: Math.floor(Math.random() * 50) + 10,
          quotations: Math.floor(Math.random() * 15) + 2,
          contractions: Math.floor(Math.random() * 8) + 1,
          certificates: Math.floor(Math.random() * 6) + 1,
        });
      }
      return fallback;
    } catch {
      return [];
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const tabLevels = {
      overview: 'Company Overview',
      products: 'Products Analysis',
      plans: 'Plans Performance',
      sessions: 'Sessions Tracking',
      quotations: 'Quotations Management',
      contracts: 'Contracts Analysis',
      certificates: 'Certificates Overview',
      subscriptions: 'Subscriptions Management',
    };
    setCurrentLevel(tabLevels[newTab] || 'Dashboard');
  };

  const handleNavigate = (item) => {
    console.log('Navigating to:', item);
  };

  const handleDrillUp = () => {
    if (breadcrumb.length > 1) {
      const newBreadcrumb = breadcrumb.slice(0, -1);
      setBreadcrumb(newBreadcrumb);
      setCurrentLevel(newBreadcrumb[newBreadcrumb.length - 1]?.label || 'Dashboard');
    }
  };

  const handleRefresh = () => loadDashboardData();

  const getFilterOptions = () => {
    switch (activeTab) {
      case 'products':
        return productPerformance.map(p => ({ value: p.productId, label: p.productName }));
      case 'plans':
        return planPerformance.map(p => ({ value: p.planId, label: p.planName }));
      default:
        return [
          { value: '7d', label: 'Últimos 7 días' },
          { value: '30d', label: 'Últimos 30 días' },
          { value: '90d', label: 'Últimos 90 días' },
        ];
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      <CompanyMetricsGrid companyId={companyId} loading={isLoading} onRefresh={handleRefresh} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ConversionFunnelChart
          data={null}
          source={funnelSource}
          companyId={companyId}
          isLoading={isLoading}
          showPercentages={true}
          timeframe={timeframe}
        />
        <CompanyDashboardCharts companyId={companyId} isLoading={isLoading} />
      </div>
      <MetricsTrendChart
        data={trendsData}
        isLoading={isLoading}
        timeframe={timeframe}
        metrics={['sessions', 'quotations', 'contractions', 'certificates']}
      />
    </div>
  );

  const renderProductsTab = () => (
    <div className="space-y-6">
      <ProductPerformanceChart data={productPerformance} isLoading={isLoading} showRevenue showConversion />
      <DetailedMetricsTable
        data={productPerformance}
        title="Detalle de Productos"
        description="Métricas detalladas por producto"
        columns={[
          { key: 'productName', label: 'Producto', sortable: true, filterable: true },
          { key: 'totalQuotations', label: 'Cotizaciones', sortable: true, type: 'number' },
          { key: 'totalContractions', label: 'Contrataciones', sortable: true, type: 'number' },
          { key: 'totalCertificates', label: 'Certificados', sortable: true, type: 'number' },
          { key: 'totalRevenueUF', label: 'Revenue', sortable: true, type: 'currency' },
          { key: 'conversionRate', label: 'Conversión', sortable: true, type: 'percentage' },
        ]}
        isLoading={isLoading}
      />
    </div>
  );

  const renderPlansTab = () => (
    <div className="space-y-6">
      <DetailedMetricsTable
        data={planPerformance}
        title="Rendimiento por Plan"
        description="Cotizaciones, contrataciones y certificados por plan"
        columns={[
          { key: 'planName', label: 'Plan', sortable: true, filterable: true },
          { key: 'productName', label: 'Producto', sortable: true, filterable: true },
          { key: 'totalQuotations', label: 'Cotizaciones', sortable: true, type: 'number' },
          { key: 'totalContractions', label: 'Contrataciones', sortable: true, type: 'number' },
          { key: 'totalCertificates', label: 'Certificados', sortable: true, type: 'number' },
          { key: 'activeSubscriptions', label: 'Suscripciones', sortable: true, type: 'number' },
          { key: 'totalRevenueUF', label: 'Revenue', sortable: true, type: 'currency' },
          { key: 'conversionRate', label: 'Conversión', sortable: true, type: 'percentage' },
        ]}
        isLoading={isLoading}
      />
    </div>
  );

  const renderMetricTab = (metricType) => {
    const commonProps = {
      companyId,
      dateRange: { start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), end: new Date() },
    };
    switch (metricType) {
      case 'sessions':      return <SessionsAnalysis {...commonProps} />;
      case 'quotations':    return <QuotationsAnalysis {...commonProps} />;
      case 'contracts':     return <ContractsAnalysis {...commonProps} />;
      case 'certificates':  return <CertificatesAnalysis {...commonProps} />;
      case 'subscriptions': return <SubscriptionsAnalysis {...commonProps} />;
      default:              return null;
    }
  };

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const companyName = company?.nombre || (companyId ? `Compañía ${companyId}` : 'Dashboard');

  return (
    <div className="container mx-auto p-6">
      <YoolPageScaffold
        header={
          <YoolPageHeader
            eyebrow="EMPRESA"
            title={companyName}
            subtitle="Métricas y análisis en tiempo real"
            actions={
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            }
          />
        }
      >
        <YoolSection>
          <CompanyDashboardNavigation
            breadcrumb={breadcrumb}
            onNavigate={handleNavigate}
            onDrillUp={handleDrillUp}
            onRefresh={handleRefresh}
            currentLevel={currentLevel}
            isLoading={isLoading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterOptions={getFilterOptions()}
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            showTabs={true}
            showTitle={false}
            availableTabs={['overview', 'products', 'plans', 'sessions', 'quotations', 'contracts', 'certificates', 'subscriptions']}
          />
        </YoolSection>

        <YoolSection divider>
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsContent value="overview" className="space-y-6">
              {renderOverviewTab()}
            </TabsContent>
            <TabsContent value="products" className="space-y-6">
              {renderProductsTab()}
            </TabsContent>
            <TabsContent value="plans" className="space-y-6">
              {renderPlansTab()}
            </TabsContent>
            <TabsContent value="sessions" className="space-y-6">
              {renderMetricTab('sessions')}
            </TabsContent>
            <TabsContent value="quotations" className="space-y-6">
              {renderMetricTab('quotations')}
            </TabsContent>
            <TabsContent value="contracts" className="space-y-6">
              {renderMetricTab('contracts')}
            </TabsContent>
            <TabsContent value="certificates" className="space-y-6">
              {renderMetricTab('certificates')}
            </TabsContent>
            <TabsContent value="subscriptions" className="space-y-6">
              {renderMetricTab('subscriptions')}
            </TabsContent>
          </Tabs>
        </YoolSection>
      </YoolPageScaffold>
    </div>
  );
};

export default CompanyDashboard;
